import React from 'react';

import * as UserDAO from 'dao/UserDAO';
import {InstitutionScheduledModal} from 'help/institutions/InstitutionModalConstants';
import {Institution} from 'institutions/InstitutionConstants';
import EntityStore from 'stores/EntityStore';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

function getInstitution(): Institution {
  return EntityStore.currentUser().institution;
}

async function dismissModal(institution: Institution): Promise<void> {
  await UserDAO.acceptInstitutionTerms(EntityStore.currentUserId!);
  LegacyModalContainer.close(InstitutionScheduledModal.LICENSED_INSTITUTION_MODAL_KEY);
}

function LicensedInstitutionBasicModalInfo({institution}: {institution: Institution}): JSX.Element {
  return (
    <div>
      <p>
        {institution.fullName} has partnered with Benchling to provide a more{' '}
        <strong>integrated, secure experience</strong> for all of its scientists.
      </p>
      <p>
        Benchling accounts with verified {institution.shortName} email addresses, like yours, gain a number of
        benefits and meet the security and backup requirements of {institution.fullName}. In order to meet
        these requirements, your account may also have additional terms and conditions required by{' '}
        {institution.fullName}, and you should contact your {institution.fullName} administrator to understand
        what additional terms may apply.
      </p>
      <p>Here's what's new for your Benchling account:</p>
      <ul>
        {institution.hasUnlimitedSpace && (
          <li>
            You have been granted <strong>unlimited space</strong>.
          </li>
        )}
        {institution.grantsElnDesktopSync && (
          <li>
            You get access to <strong>Benchling Sync</strong>, a feature that allows you to open a file
            attachment in its desktop application and automatically syncs edits to your Notebook.
          </li>
        )}
        <li>
          Your projects will <strong>automatically be backed up</strong> to {institution.shortName} servers on
          a weekly basis, eliminating the need for periodic data exports.
        </li>
        <li>
          You will log in directly and seamlessly through <strong>Single Sign-On</strong>.
        </li>
        <li>
          {institution.fullName} will periodically receive information about your account — your email
          address, organizations, and activity — to ensure the quality of your Benchling experience.
        </li>
      </ul>
    </div>
  );
}

interface LicensedInstitutionModalProps {
  institution: Institution;
  buttons: Array<ModalButton>;
  onClose: () => void;
  blockDismiss?: boolean;
}

function LicensedInstitutionModal({
  institution,
  buttons,
  onClose,
  blockDismiss,
}: LicensedInstitutionModalProps): JSX.Element {
  return (
    <Modal
      enableRedesign={true}
      title={`${institution.fullName}: Your Licensed Institution`}
      buttons={buttons}
      onRequestClose={onClose}
      closeable={!blockDismiss}
      closeableOnBackgroundClick={!blockDismiss}
      closeableOnEscapeKey={!blockDismiss}
    >
      <LicensedInstitutionBasicModalInfo institution={institution} />
    </Modal>
  );
}

/**
 * Renders a modal describing the features of the institution that logs when it has been dismissed. Pulls
 * information on the current institution from the store.
 */
export function LicensedInstitutionTermsModal(): JSX.Element {
  const institution = getInstitution();
  return (
    <LicensedInstitutionModal
      institution={institution}
      onClose={() => {
        dismissModal(institution);
      }}
      blockDismiss={true}
      buttons={[
        {
          text: 'OK',
          style: 'default',
          onClick: () => {
            dismissModal(institution);
          },
        },
      ]}
    />
  );
}

/**
 * Renders an informational message about this institution, with a link that triggers the modal with more
 * information. Pulls information on the current information through an input prop.
 */
// Currently intended for use on the user preferences page.
export function LicensedInstitutionInfoLink({institution}: {institution: Institution}): JSX.Element {
  return (
    <div>
      <p>
        As a member of <strong>{institution.fullName}</strong>, you have special{' '}
        <a
          onClick={() =>
            LegacyModalContainer.open(
              InstitutionScheduledModal.LICENSED_INSTITUTION_MODAL_KEY,
              <LicensedInstitutionModal
                institution={institution}
                buttons={[]}
                onClose={() => {
                  LegacyModalContainer.close(InstitutionScheduledModal.LICENSED_INSTITUTION_MODAL_KEY);
                }}
              />
            )
          }
        >
          product, security, and privacy features
        </a>{' '}
        enabled on your account.
      </p>
    </div>
  );
}
